<template>
  <div>
    <b-card no-body>
      <p class="mt-1 ml-2">
        <feather-icon
          icon="InfoIcon"
          size="18"
          class="mr-50"
        /> El inventario que se muestra es un inventario por variante de producto.
      </p>

      <div class="m-2">
        <b-row>
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label>Mostrar</label>
            <v-select
              v-model="perPage"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block ml-50 mr-1"
            />
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block mr-1"
                placeholder="Buscar..."
              />
            </div>
          </b-col>
        </b-row>
      </div>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            v-if="meta"
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">
              Mostrando del {{ meta.from }} al {{ meta.to }} de {{ meta.total }} registros
            </span>

            <download-excel
              v-if="rows.length"
              class="btn"
              :data="rows"
              :fields="excelFields"
              worksheet="Inventario"
              :name="'inventario_' + getCurrentDate() + '.xls'"
            >
              <feather-icon icon="DownloadIcon" />
              Descargar Excel
            </download-excel>
          </b-col>
        </b-row>
      </div>

      <b-table
        class="position-relative"
        hover
        responsive
        small
        :busy="tableIsBusy"
        primary-key="id"
        show-empty
        empty-text="No se encontraron registros coincidentes"
        :items="rows"
        :fields="fields"
        :per-page="perPage"
      >
        <template #table-busy>
          <div class="text-center text-dark my-2">
            <b-spinner class="align-middle" />
          </div>
        </template>

        <template #cell(product)="data">
          <div class="d-flex align-items-center">
            <div
              v-if="data.item.image"
              class="mr-50"
            >
              <img
                :src="getImageUrl(data.item.image.path)"
                :alt="data.item.image.alt_text || 'Imagen del producto'"
                class="thumb cursor-pointer"
                @error="handleImageError"
                @click="data.item.image && ($bvModal.show('modal-image'), selectedImage = data.item.image)"
              >
            </div>
            <div>
              <div class="font-weight-bold">
                {{ data.item.product }} - {{ data.item.color }}
              </div>
              <small>{{ data.item.description }}</small>
            </div>
          </div>
        </template>

        <template #cell(sku)="data">
          <b-badge variant="light-secondary">
            {{ data.item.sku }}
          </b-badge>
        </template>

        <template #cell(available_boxes)="data">
          <b-badge :variant="getInventoryVariant(data.item.available_boxes)">
            {{ data.item.available_boxes }}
          </b-badge>
        </template>
      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="12"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-if="meta"
              v-model="currentPage"
              :total-rows="meta.total"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- Modal para mostrar imagen en grande -->
    <b-modal
      id="modal-image"
      hide-footer
      centered
      size="lg"
      :title="selectedImage.alt_text || 'Imagen del producto'"
    >
      <div class="text-center">
        <img
          :src="getImageUrl(selectedImage.path)"
          :alt="selectedImage.alt_text || 'Imagen del producto'"
          class="img-fluid"
          @error="handleImageError"
        >
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from '@axios'
import { buildStorageUrl, NO_IMAGE_URL } from '@/utils/urls'
import vSelect from 'vue-select'
import downloadExcel from 'vue-json-excel'
import Ripple from 'vue-ripple-directive'

export default {
  components: { vSelect, downloadExcel },
  directives: { Ripple },

  data() {
    return {
      tableIsBusy: true,
      searchQuery: '',
      sortField: 'id',
      sortDesc: 'asc',
      perPage: 25,
      perPageOptions: [5, 10, 25, 50, 100, 'Todos'],
      currentPage: 1,
      searchQueryTimeout: null,
      fields: [
        { key: 'product', label: 'Producto' },
        { key: 'sku', label: 'SKU por variante' },
        { key: 'size', label: 'Talla' },
        { key: 'available_boxes', label: 'Disponible' },
        { key: 'reserved_boxes', label: 'Reservado' },
        { key: 'sold_boxes', label: 'Vendido' },
      ],
      rows: [],
      meta: null,
      selectedImage: {
        path: '',
        alt_text: '',
      },
    }
  },

  computed: {
    excelFields() {
      return {
        Producto: 'product',
        Descripción: 'description',
        Color: 'color',
        SKU: 'sku',
        Talla: 'size',
        Disponible: 'available_boxes',
        Reservado: 'reserved_boxes',
        Vendido: 'sold_boxes',
      }
    },
  },

  watch: {
    perPage() {
      this.fetchData()
    },
    currentPage() {
      this.fetchData()
    },
    searchQuery() {
      // Limpiar el timeout anterior
      clearTimeout(this.searchQueryTimeout)

      if (!this.searchQuery || this.searchQuery.trim() === '') {
        // Si esta vacío, buscar inmediatamente
        this.currentPage = 1
        this.tableIsBusy = true
        this.fetchData()
      } else {
        // si hay texto, esperar 300ms para no saturar el servidor
        this.searchQueryTimeout = setTimeout(() => {
          this.currentPage = 1
          this.tableIsBusy = true
          this.fetchData()
        }, 300)
      }
    },
  },

  created() {
    this.fetchData()
  },

  methods: {
    async fetchData() {
      if (this.perPage === 'Todos') {
        this.perPage = this.meta ? this.meta.total : 1000
      }
      this.tableIsBusy = true
      try {
        const response = await axios.get(
          `product-variant?perPage=${this.perPage}&page=${this.currentPage}&query=${this.searchQuery}&sortField=${this.sortField}&sortDesc=${this.sortDesc}`,
        )
        this.meta = response.data.meta
        const data = response.data.data || []
        // map to rows for table
        this.rows = data.map(v => ({
          id: v.id,
          product: v.product,
          color: v.color,
          description: v.description,
          sku: v.sku,
          presentation: v.presentation,
          size: v.size,
          available_boxes: v.stock || 0,
          reserved_boxes: (v.stock_detail && v.stock_detail.reserved_boxes) || 0,
          sold_boxes: (v.stock_detail && v.stock_detail.sold_boxes) || 0,
          image: v.image || null,
        }))
      } catch (error) {
        this.showErrors(error)
      } finally {
        this.tableIsBusy = false
      }
    },

    getImageUrl(path) {
      if (!path) return NO_IMAGE_URL
      return buildStorageUrl(path)
    },

    getCurrentDate() {
      return new Date().toISOString().slice(0, 10)
    },

    handleImageError(event) {
      const img = event && event.target
      if (img) img.setAttribute('src', NO_IMAGE_URL)
    },

    getInventoryVariant(value) {
      if (value > 5) return 'light-success'
      if (value > 0) return 'light-warning'
      return 'light-danger'
    },

    showErrors(error) {
      if (error?.response?.data?.message) {
        this.$bvToast.toast(error.response.data.message, {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } else {
        this.$bvToast.toast('Ha ocurrido un error inesperado', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector { width: 90px; }
.thumb { width: 32px; height: 32px; object-fit: cover; border-radius: 4px; border: 1px solid #ddd; }
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
